import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  format,
  isValid,
  parse,
  parseISO,
  startOfMonth,
  subDays,
} from 'date-fns';
import { clamp } from '../../util/functions';

/**
 * Describes a single filter field that can be rendered in the filter panel.
 */
export interface FilterFieldConfig {
  /** The filter key that maps to the API request (e.g. 'titleContains', 'bpmGreaterThan').
   *  For `range`, this is a logical id (e.g. 'releaseYear'), NOT an API key. */
  key: string;
  /** The display label for this field */
  label: string;
  /** The input type. `threshold` is a single-handle min slider. */
  type: 'text' | 'number' | 'date' | 'select' | 'range' | 'threshold';
  /** For select type, the available options */
  options?: { value: string; label: string }[];
  /** Group multiple fields in the same row */
  group?: string;
  /** Flex sizing within a row */
  flex?: number;
  /** range only: API param for the lower bound (strict `>`), e.g. 'releaseYearGreaterThan' */
  minKey?: string;
  /** range only: API param for the upper bound (strict `<`), e.g. 'releaseYearLessThan' */
  maxKey?: string;
  /** range/threshold: slider track minimum (a real year, or 0 for threshold) */
  min?: number;
  /** range/threshold: slider track maximum */
  max?: number;
  /** range/threshold: slider step (default 1) */
  step?: number;
  /** range only: display + conversion scale. Defaults to 'year'. */
  scale?: 'year' | 'month';
  /** month scale only: calendar year of slider index 0 */
  anchorYear?: number;
  /** month scale only: 0-based calendar month of slider index 0 (April = 3) */
  anchorMonth?: number;
}

type Filters = Record<string, unknown> | undefined;

/** The slider track's [min, max] bounds, defaulting to 0 when unset. */
const trackBounds = (field: FilterFieldConfig): [number, number] => [
  field.min ?? 0,
  field.max ?? 0,
];

/** First day of the month at slider index 0, for a month-scale field. */
const monthAnchor = (field: FilterFieldConfig): Date =>
  new Date(field.anchorYear ?? 1970, field.anchorMonth ?? 0, 1);

/**
 * Display text for a slider value. Year scale → the plain number; month scale →
 * "Month Year" ('full', e.g. "June 2026") or "Mon Year" ('short', e.g. "Jun 2026").
 */
export function formatRangeValue(
  field: FilterFieldConfig,
  n: number,
  variant: 'full' | 'short' = 'full',
): string {
  if (field.scale === 'month') {
    const date = addMonths(monthAnchor(field), n);
    return format(date, variant === 'short' ? 'MMM yyyy' : 'MMMM yyyy');
  }
  return String(n);
}

/**
 * The current [low, high] slider position derived from stored filters.
 * Year scale reverses the inclusive ±1 integer offset; month scale parses the
 * stored boundary dates back to whole-month indices. Falls back to the track
 * bounds for any side that is not set, and clamps into [min, max].
 */
export function sliderValueFromFilters(
  filters: Filters,
  field: FilterFieldConfig,
): [number, number] {
  const [min, max] = trackBounds(field);
  const gt = field.minKey ? filters?.[field.minKey] : undefined;
  const lt = field.maxKey ? filters?.[field.maxKey] : undefined;

  if (field.scale === 'month') {
    const anchor = monthAnchor(field);
    const low =
      typeof gt === 'string'
        ? clamp(
            differenceInCalendarMonths(addDays(parseISO(gt), 1), anchor),
            min,
            max,
          )
        : min;
    const high =
      typeof lt === 'string'
        ? clamp(
            differenceInCalendarMonths(subDays(parseISO(lt), 1), anchor),
            min,
            max,
          )
        : max;
    return [low, high];
  }

  const low = typeof gt === 'number' ? clamp(gt + 1, min, max) : min;
  const high = typeof lt === 'number' ? clamp(lt - 1, min, max) : max;
  return [low, high];
}

/**
 * The filter patch for a committed [low, high] slider value. A handle at a
 * track extreme drops that key entirely. Year scale uses the inclusive ±1
 * integer offset; month scale writes inclusive whole-month boundary dates
 * (lower = day before the 1st of month `low`; upper = 1st of the month after
 * `high`) for the API's strict `>` / `<` date comparisons.
 */
export function filtersPatchFromSliderValue(
  [low, high]: [number, number],
  field: FilterFieldConfig,
): Record<string, string | number | undefined> {
  const [min, max] = trackBounds(field);
  const patch: Record<string, string | number | undefined> = {};

  if (field.scale === 'month') {
    const anchor = monthAnchor(field);
    if (field.minKey) {
      patch[field.minKey] =
        low > min
          ? format(subDays(addMonths(anchor, low), 1), 'yyyy-MM-dd')
          : undefined;
    }
    if (field.maxKey) {
      patch[field.maxKey] =
        high < max
          ? format(addMonths(anchor, high + 1), 'yyyy-MM-dd')
          : undefined;
    }
    return patch;
  }

  if (field.minKey) patch[field.minKey] = low > min ? low - 1 : undefined;
  if (field.maxKey) patch[field.maxKey] = high < max ? high + 1 : undefined;
  return patch;
}

/** One chip in the "Active Filters" row. */
export interface ActiveFilterChip {
  /** Stable id (React key). For a range field this is the field's logical key. */
  id: string;
  /** Display text, e.g. "Release Year: 1990 – 2010" or "Title: love". */
  label: string;
  /** Filter keys this chip represents; deleting the chip clears all of them. */
  keys: string[];
}

export const isActiveValue = (v: unknown): boolean => {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

const humanizeKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

/**
 * The list of active-filter chips. Range fields collapse their two API keys
 * into a single chip (real years, offset reversed); every other active key
 * becomes one chip, labelled from its field config or a humanized key.
 */
export function getActiveFilterChips(
  filters: Filters,
  fields: FilterFieldConfig[],
): ActiveFilterChip[] {
  if (!filters) return [];
  const chips: ActiveFilterChip[] = [];
  const claimed = new Set<string>();

  for (const field of fields) {
    if (field.type !== 'range') continue;
    const keys = [field.minKey, field.maxKey].filter((k): k is string =>
      Boolean(k),
    );
    // Claim both keys unconditionally so a range field's API keys never
    // surface as standalone chips, even when the chip itself isn't emitted.
    keys.forEach((k) => claimed.add(k));
    if (!keys.some((k) => isActiveValue(filters[k]))) continue;
    const [low, high] = sliderValueFromFilters(filters, field);
    chips.push({
      id: field.key,
      label: `${field.label}: ${formatRangeValue(field, low)} – ${formatRangeValue(field, high)}`,
      keys,
    });
  }

  for (const [key, value] of Object.entries(filters)) {
    if (claimed.has(key) || !isActiveValue(value)) continue;
    const field = fields.find((f) => f.key === key);
    const label = field ? field.label : humanizeKey(key);
    // For selects, show the chosen option's label (e.g. "Clean") rather than
    // its raw stored value (e.g. "NotExplicit").
    const displayValue =
      field?.type === 'select'
        ? (field.options?.find((o) => o.value === value)?.label ??
          String(value))
        : String(value);
    chips.push({ id: key, label: `${label}: ${displayValue}`, keys: [key] });
  }

  return chips;
}

/**
 * Whether a field currently constrains the results. Text/select/number/date
 * fields are active when their key holds a non-empty value; range fields are
 * active when either bound key is set.
 */
export function isFieldActive(
  filters: Filters,
  field: FilterFieldConfig,
): boolean {
  if (!filters) return false;
  if (field.type === 'range') {
    const gt = field.minKey ? filters[field.minKey] : undefined;
    const lt = field.maxKey ? filters[field.maxKey] : undefined;
    return isActiveValue(gt) || isActiveValue(lt);
  }
  return isActiveValue(filters[field.key]);
}

/**
 * Parse a typed month label ("Jun 2026" / "June 2026") into the slider's
 * whole-month index for a month-scale field. Returns null when blank or
 * unparseable.
 */
export function parseMonthInput(
  field: FilterFieldConfig,
  text: string,
): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const anchor = monthAnchor(field);
  for (const fmt of ['MMM yyyy', 'MMMM yyyy']) {
    const parsed = parse(trimmed, fmt, anchor);
    if (isValid(parsed)) {
      return differenceInCalendarMonths(startOfMonth(parsed), anchor);
    }
  }
  return null;
}
